import {
  Component,
  Input,
  OnChanges,
  AfterViewInit,
  OnDestroy,
  ElementRef,
  ViewChild,
  SimpleChanges,
  inject,
  ChangeDetectionStrategy
} from '@angular/core';
import { CommonModule, CurrencyPipe } from '@angular/common';
import * as d3 from 'd3';
import { BrowserModule } from '@angular/platform-browser';
import { ThemeService, Theme } from '../../../../core/services/theme.service';
import { Subscription } from 'rxjs';

export interface ItooltipExtra {
  key: string;
  label: string;
  isAmount: boolean;
}
@Component({
  selector: 'app-multi-line-chart',
  templateUrl: './multi-line-chart.component.html',
  providers: [CurrencyPipe],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: true,
})
export class MultiLineChartComponent
  implements OnChanges, AfterViewInit, OnDestroy
{
  @Input() showAllXLabels: boolean = false;
  @Input() xAxisSmartLabelAnchors: boolean = false;
  @Input() showXAxisGrid: boolean = false;
  @Input() xAxisShowOnlyFirstLast: boolean = false;
  @Input() showAllXticks: boolean = false;
  @Input() data1: any[] = [];
  @Input() data2: any[] = [];
  @Input() data3: any[] = [];
  @Input() data4: any[] = [];
  @Input() data5: any[] = [];
  @Input() data6: any[] = [];
  @Input() data7: any[] = [];

  @Input() valueKey: string = '';
  @Input() unit: string = 'week';
  @Input() isAmount: boolean = false;
  @Input() valueFormat: 'currency' | 'number' | 'percent' = 'number'; // Format type for tooltip values
  @Input() tooltipExtraData: ItooltipExtra[] = [];

  // --- Styling Inputs (deprecated - now managed by theme) ---
  @Input() primaryLineColor: string = '';
  @Input() secondaryLineColor: string = '';
  @Input() tertiaryLineColor: string = '';
  @Input() quaternaryLineColor: string = '';
  @Input() quinaryLineColor: string = '';
  @Input() senaryLineColor: string = '';
  @Input() septenaryLineColor: string = '';
  @Input() octonaryLineColor: string = '';
  @Input() nonaryLineColor: string = '';
  @Input() denaryLineColor: string = '';
  @Input() axisColor: string = '';
  @Input() axisTickColor: string = '';
  @Input() axisLabelColor: string = '';

  // --- Feature Flag Inputs ---
  @Input() showXAxis: boolean = true;
  @Input() showYAxis: boolean = true;
  @Input() yAxisShowOnlyFirstLast: boolean = false;
  @Input() showYAxisGrid: boolean = true;
  @Input() showCircleStrokes: boolean = false;
  @Input() showCircleStrokesWithStroke: boolean = false;
  @Input() strokeWithLineColor: boolean = false;

  // Custom margins { top, right, bottom, left }
  @Input() marginProps: any = {};
  @Input() setBg: boolean = false;
  @Input() showAllYticks: boolean = false;
  @Input() showYAxisPath: boolean = false;
  isReverse: Boolean = false;
  @Input() curveType:
    | 'curveLinear'
    | 'curveMonotoneX'
    | 'curveStep'
    | 'curveStepBefore'
    | 'curveStepAfter' = 'curveLinear';

  @ViewChild('chartContainer', { static: true })
  private chartContainer!: ElementRef;
  private resizeObserver!: ResizeObserver;
  private themeSubscription?: Subscription;
  private currentTheme: Theme = 'light';

  private themeService = inject(ThemeService);

  constructor(private currencyPipe: CurrencyPipe) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (
      (changes['data1'] ||
        changes['data2'] ||
        changes['data3'] ||
        changes['data4'] ||
        changes['data5'] ||
        changes['data6'] ||
        changes['data7']) &&
      this.chartContainer?.nativeElement
    ) {
      if (changes['data2'] && changes['data2'].currentValue?.length == 0) {
        this.isReverse = false;
      }

      // No need to call createChart here if ResizeObserver is active,
      // but let's keep it for data-only updates if the container size hasn't changed.
      this.createChart();
    }
  }

  ngAfterViewInit(): void {
    // Subscribe to theme changes
    this.currentTheme = this.themeService.getTheme();
    this.themeSubscription = this.themeService.theme$.subscribe((theme) => {
      this.currentTheme = theme;
      this.createChart(); // Redraw chart with new theme colors
    });

    this.resizeObserver = new ResizeObserver(() => {
      // Use requestAnimationFrame to debounce resize events and prevent "ResizeObserver loop limit exceeded"
      window.requestAnimationFrame(() => {
        if (this.chartContainer?.nativeElement) {
          this.createChart();
        }
      });
    });

    if (this.chartContainer?.nativeElement) {
      this.resizeObserver.observe(this.chartContainer.nativeElement);
    }
  }

  ngOnDestroy(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.themeSubscription) {
      this.themeSubscription.unsubscribe();
    }
  }

  // Get theme-aware colors
  private getThemeColors() {
    const isDark = this.currentTheme === 'dark';
    return {
      primaryLine: this.primaryLineColor || (isDark ? '#AD46FF' : '#8b5cf6'), // purple-400 / purple-600
      secondaryLine:
        this.secondaryLineColor || (isDark ? '#A1A1AA' : '#7D7D93'), // slate-500 / gray-400
      tertiaryLine: this.tertiaryLineColor || '#4EA1FF', // slate-500 / gray-400
      quaternaryLine: this.quaternaryLineColor || '#FF7AA2', // slate-500 / gray-400
      quinaryLine: this.quinaryLineColor || '#14D18E', // slate-500 / gray-400
      senaryLine: this.senaryLineColor || '#FFD76B', // slate-500 / gray-400
      septenaryLine: this.septenaryLineColor || '#8B5CF6', // slate-500 / gray-400
      octonaryLine: this.octonaryLineColor || '#F59E0B', // slate-500 / gray-400
      nonaryLine: this.nonaryLineColor || '#F05050', // slate-500 / gray-400
      denaryLine: this.denaryLineColor || '#15B268', // slate-500 / gray-400
      axis: this.axisColor || (isDark ? '#47474e' : '#DFE5ED'), // slate-600 / gray-300
      axisTick: this.axisTickColor || (isDark ? '#A1A1AA' : '#7D7D93'), // slate-300 / dark-gray
      axisLabel: this.axisLabelColor || (isDark ? '#d4d4d8' : '#131921'), // slate-300 / dark-gray
      tooltipBg: isDark ? '#1e1e21' : '#ffffff', // slate-800 / white
      tooltipBorder: isDark ? '#47474e' : '#e5e7eb', // slate-600 / gray-200
      tooltipText: isDark ? '#f4f4f5' : '#000000', // slate-100 / black
      tooltipSecondaryText: isDark ? '#a1a1aa' : '#6b7280', // slate-400 / gray-500
      gridLine: isDark ? '#47474e' : '#DFE5ED', // same as axis color
      hoverLine: isDark ? '#85858e' : '#A2A8B0', // slate-500 / gray-400
      badgePositiveBg: isDark ? '#064e3b' : '#d1fae5', // emerald-900 / emerald-100
      badgePositiveBorder: isDark ? '#10b981' : '#34d399', // emerald-500 / emerald-400
      badgePositiveText: isDark ? '#6ee7b7' : '#047857', // emerald-300 / emerald-700
      badgeNegativeBg: isDark ? '#7c2d12' : '#fed7aa', // orange-900 / orange-100
      badgeNegativeBorder: isDark ? '#f97316' : '#fb923c', // orange-500 / orange-400
      badgeNegativeText: isDark ? '#fdba74' : '#c2410c', // orange-300 / orange-700
      divider: isDark ? '#47474e' : '#cbd5e1', // slate-600 / slate-300
      backgroundGradient: isDark
        ? 'linear-gradient(180deg, rgba(15, 23, 42, 0.8) 35%, rgba(30, 41, 59, 0.6) 96%)' // slate-900/800
        : 'linear-gradient(180deg,rgba(242, 245, 248, 1) 35%, rgba(255, 255, 255, 1) 96%)',
      xAxisOverlayBg: isDark
        ? 'rgba(30, 41, 59, 0.5)'
        : 'rgba(242, 245, 248, 0.5)', // slate-800 / light gray
    };
  }

  private timezoneDate(dateStr: string): Date {
    if (this.unit === 'hour') {
      const [year, month, day] = dateStr.split(' ')[0].split('-').map(Number);
      const [hour, minute, second] = dateStr
        .split(' ')[1]
        .split(':')
        .map(Number);
      return new Date(Date.UTC(year, month - 1, day, hour, minute, second));
    } else {
      const [year, month, day] = dateStr.split(' ')[0].split('-').map(Number);
      return new Date(Date.UTC(year, month - 1, day));
    }
  }

  private unitFormatter(): (start: Date, end?: Date) => string {
    const currentYear = new Date().getUTCFullYear();

    return (start: Date, end?: Date): string => {
      const year = start.getUTCFullYear();
      const endYear = end?.getUTCFullYear() ?? year;
      const isSameYearAsCurrent = year === endYear && year === currentYear;

      switch (this.unit) {
        case 'year':
          return d3.utcFormat('%Y')(start);
        case 'quarter':
          if (!end) {
            return d3.utcFormat('%d %b %Y')(start);
          }
          if (year !== endYear) {
            return `${d3.utcFormat('%b %d, %Y')(start)} - ${d3.utcFormat(
              '%b %d, %Y'
            )(end)}`;
          }
          if (start.getUTCMonth() !== end.getUTCMonth()) {
            return `${d3.utcFormat('%b %d')(start)} - ${d3.utcFormat(
              '%b %d, %Y'
            )(end)}`;
          }
          return `${d3.utcFormat('%b %d')(start)} - ${d3.utcFormat('%d, %Y')(
            end
          )}`;
        case 'month':
          return d3.utcFormat('%b %Y')(start);
        case 'week':
          if (!end) {
            return d3.utcFormat('%d %b %Y')(start);
          }
          if (year !== endYear) {
            return `${d3.utcFormat('%b %d, %Y')(start)} - ${d3.utcFormat(
              '%b %d, %Y'
            )(end)}`;
          }
          if (start.getUTCMonth() !== end.getUTCMonth()) {
            return `${d3.utcFormat('%b %d')(start)} - ${d3.utcFormat(
              '%b %d, %Y'
            )(end)}`;
          }
          return `${d3.utcFormat('%b %d')(start)} - ${d3.utcFormat('%d, %Y')(
            end
          )}`;
        case 'day':
          return d3.utcFormat(isSameYearAsCurrent ? '%d %b' : '%d %b %Y')(
            start
          );
        case 'hour':
          return d3.utcFormat('%H:%M')(start);
        default:
          return d3.utcFormat('%Y-%m-%d')(start);
      }
    };
  }

  private getWidestLabelWidth(labels: string[], font = '12px lato'): number {
    const context = document.createElement('canvas').getContext('2d');
    if (!context) return 40;
    context.font = font;
    return Math.max(...labels.map((label) => context.measureText(label).width));
  }

  private calculatePercentChange(current: any, previous: any): number {
    const currentVal = parseFloat(current);
    const previousVal = parseFloat(previous);
    if (isNaN(currentVal) || isNaN(previousVal) || previousVal === 0) return 0;
    return ((currentVal - previousVal) / Math.abs(previousVal)) * 100;
  }

  private customToolTipHtml(
    hasPreviousData: boolean,
    percentChangeNum: number,
    currentUnitPeriod: string,
    previousUnitPeriod: string,
    currentValue: number,
    previousValue: number,
    isAmount: boolean,
    extraData: any[] = []
  ): string {
    const colors = this.getThemeColors();

    const formatValue = (val: number) => {
      if (val == null || isNaN(val)) return 'N/A';

      switch (this.valueFormat) {
        case 'currency':
          // Format with $ prefix and comma separators, up to 2 decimals
          const formatted = val.toLocaleString('en-US', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
          });
          return `$${formatted}`;
        case 'percent':
          return `${val.toFixed(1)}%`;
        default:
          return val.toLocaleString('en-US');
      }
    };

    let percentChangeHtml = '';
    if (hasPreviousData) {
      const isPositive = percentChangeNum > 0;
      const badgeClass = isPositive
        ? `bg-[${colors.badgePositiveBg}] border-[${colors.badgePositiveBorder}] text-[${colors.badgePositiveText}]`
        : `bg-[${colors.badgeNegativeBg}] border-[${colors.badgeNegativeBorder}] text-[${colors.badgeNegativeText}]`;
      const badgeText = `${isPositive ? '+' : ''}${percentChangeNum.toFixed(
        1
      )}%`;

      percentChangeHtml = `
        <div class="flex justify-end">
          <div class="border py-0.5 px-2 rounded-md font-semibold text-xs" style="
            background-color: ${
              isPositive ? colors.badgePositiveBg : colors.badgeNegativeBg
            };
            border-color: ${
              isPositive
                ? colors.badgePositiveBorder
                : colors.badgeNegativeBorder
            };
            color: ${
              isPositive ? colors.badgePositiveText : colors.badgeNegativeText
            };
            padding: 0.15rem 0.25rem !important;
          ">${badgeText}</div>
        </div>`;
    }

    let previousPeriodHtml = '';
    if (hasPreviousData) {
      previousPeriodHtml = `
        <div class="flex justify-between items-center gap-5 ${
          this.isReverse ? 'order-1' : 'order-2'
        }">
          <div class="flex justify-start items-center gap-2">
            <div style="background-color: ${
              !this.isReverse ? colors.secondaryLine : colors.primaryLine
            }" class="w-2 h-2 rounded-full"></div>
            <div class="font-medium text-xs" style="color: ${
              colors.tooltipSecondaryText
            }">${previousUnitPeriod}</div>
          </div>
          <div class="font-semibold text-xs" style="color: ${
            colors.tooltipText
          }">${formatValue(previousValue)}</div>
        </div>`;
    }

    let extraValuesHtml = '';
    if (!hasPreviousData && extraData && extraData.length > 0) {
      const extraRows = extraData
        .map(({ label, value, isAmount: itemIsAmount = false }) => {
          // For extra data items, respect individual isAmount flags
          const displayValue = itemIsAmount
            ? `$${value.toLocaleString('en-US', {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
              })}`
            : value;

          return `
        <div class="flex justify-between items-center gap-5">
            <div class="font-semibold" style="font-size: 12px; color: ${colors.tooltipSecondaryText}">${label}</div>
            <div class="font-semibold" style="font-size: 12px; color: ${colors.tooltipText}">${displayValue}</div>
        </div>`;
        })
        .join('');

      extraValuesHtml = hasPreviousData
        ? `<div class="h-[1px] my-1" style="background-color: ${colors.divider}"></div>${extraRows}`
        : '';
    }

    const finalHtml = `
    <div class="shadow-lg px-2 py-1 flex flex-col gap-1.5 rounded-md border" style="
      background-color: ${colors.tooltipBg};
      border-color: ${colors.tooltipBorder};
      padding: 0.5rem !important;
    ">
    ${
      percentChangeHtml
        ? `<div class="flex flex-col justify-between gap-0.5">
          ${percentChangeHtml}
          ${
            percentChangeHtml
              ? `<div class="h-px my-1" style="background-color: ${colors.divider}"></div>`
              : ''
          }
        </div>`
        : ''
    }
      <div class="flex justify-between items-center gap-5 ${
        this.isReverse ? 'order-2' : 'order-1'
      }">
        <div class="flex justify-start items-center gap-2">
          <div style="background-color: ${
            this.isReverse ? colors.secondaryLine : colors.primaryLine
          }" class="w-2 h-2 rounded-full"></div>
          <div class="font-medium text-xs" style="color: ${
            colors.tooltipSecondaryText
          }">${currentUnitPeriod}</div>
        </div>
        <div class="font-semibold text-xs" style="color: ${
          colors.tooltipText
        }">${formatValue(currentValue)}</div>
      </div>
      ${previousPeriodHtml}
      ${extraValuesHtml}
    </div>`;
    return finalHtml;
  }

  private yearIndexModify(
    currentData: any[],
    previousData: any[]
  ): { currentData: any[]; previousData: any[] } {
    if (!previousData.length || currentData.length === previousData.length) {
      return { currentData, previousData };
    }

    const isCurrentShorter = currentData.length < previousData.length;
    const shorterData = isCurrentShorter ? [...currentData] : [...previousData];
    const longerData = isCurrentShorter ? previousData : currentData;

    if (longerData.length - shorterData.length !== 1) {
      return { currentData, previousData };
    }

    const insertionIndex = 59;

    if (shorterData.length < insertionIndex) {
      return { currentData, previousData };
    }

    const sampleObject = shorterData[insertionIndex - 1];
    const dummyLeapDay = { ...sampleObject };

    for (const key in dummyLeapDay) {
      if (Object.prototype.hasOwnProperty.call(dummyLeapDay, key)) {
        if (
          key.toLowerCase().includes('date') ||
          key.toLowerCase().includes('label')
        ) {
          dummyLeapDay[key] = '-';
        } else if (key.toLowerCase().includes('value')) {
          dummyLeapDay[key] = 'no data';
        } else if (typeof dummyLeapDay[key] === 'number') {
          dummyLeapDay[key] = 0;
        } else {
          dummyLeapDay[key] = null;
        }
      }
    }

    shorterData.splice(insertionIndex, 0, dummyLeapDay);

    if (isCurrentShorter) {
      return { currentData: shorterData, previousData };
    } else {
      return { currentData, previousData: shorterData };
    }
  }

  private createChart(): void {
    const container = this.chartContainer.nativeElement;
    if (!container) return;

    // if there's no data at all, clear and exit
    const rawInputs = [
      this.data1,
      this.data2,
      this.data3,
      this.data4,
      this.data5,
      this.data6,
      this.data7,
    ];
    const nonEmptySets = rawInputs.filter(
      (arr) => Array.isArray(arr) && arr.length > 0
    );
    if (!nonEmptySets.length) {
      d3.select(container).selectAll('*').remove();
      while (container.firstChild) container.removeChild(container.firstChild);
      return;
    }

    // clean container
    d3.select(container).selectAll('*').remove();
    while (container.firstChild) container.removeChild(container.firstChild);

    const colors = this.getThemeColors();
    const formatter = this.unitFormatter();

    // Convert incoming raw datasets into normalized series (Date objects, label, value, name, tooltipExtraData)
    const seriesSets = nonEmptySets.map((dataset) =>
      dataset
        .map((ds) => {
          const tooltipExtra = this.tooltipExtraData?.length
            ? this.tooltipExtraData
                .filter((extra) =>
                  Object.prototype.hasOwnProperty.call(ds, extra.key)
                )
                .map((extra) => ({
                  label: extra.label,
                  value: ds[extra.key],
                  isAmount: extra.isAmount,
                }))
            : [];

          return {
            raw: ds,
            start: this.timezoneDate(ds?.start),
            end: this.timezoneDate(ds?.end),
            value:
              typeof ds[this.valueKey] === 'number'
                ? ds[this.valueKey]
                : Number(ds[this.valueKey]) || 0,
            label: formatter(
              this.timezoneDate(ds.start),
              this.timezoneDate(ds.end)
            ),
            tooltipExtraData: tooltipExtra,
            name: ds.key || '',
          };
        })
        .sort((a, b) => a.start.getTime() - b.start.getTime())
    );

    // Primary series for X-axis alignment (first non-empty dataset)
    const primary = seriesSets[0];

    // Align other series to primary indices: ensure same length by mapping to primary indexes
    const alignedSeries = seriesSets.map((set, idx) => {
      if (idx === 0) return set;
      // map each element of primary: try to pick element at same index if exists, otherwise fallback to last known
      return primary.map((p, i) => {
        const s = set[i];
        if (s && s.start) {
          return { ...s, start: p.start }; // align time to primary
        }
        // if missing, create placeholder (keeps the index alignment)
        return {
          raw: null,
          start: p.start,
          end: p.end ?? p.start,
          value: NaN,
          label: p.label,
          tooltipExtraData: [],
          name: set[0]?.name || `Series ${idx + 1}`,
        };
      });
    });

    // apply yearIndexModify against primary to handle leap insertion holes if applicable
    // yearIndexModify expects (currentData, previousData) - we'll use previousData as empty array.
    const yi = this.yearIndexModify(alignedSeries[0], []);
    const primaryAdjusted = yi.currentData;

    // If yearIndexModify injected a placeholder, replicate that insertion into all series so indices remain aligned
    if (yi.currentData !== alignedSeries[0]) {
      // find differences in length
      const delta = primaryAdjusted.length - alignedSeries[0].length;
      if (delta > 0) {
        // We assume yearIndexModify inserted exactly one placeholder at an index; find index by comparing starts
        const insertedIndex = primaryAdjusted.findIndex((d, i) => {
          const orig = alignedSeries[0][i];
          // placeholder likely has 'no data' or non-number; compare label or typeof value
          return !orig || orig.start.getTime() !== d.start.getTime();
        });
        if (insertedIndex >= 0) {
          // insert a placeholder into each aligned series at insertedIndex
          alignedSeries.forEach((s, si) => {
            const sample = s[insertedIndex - 1] ?? s[0];
            const dummy = sample
              ? { ...sample, label: '-', value: 'no data' as any, raw: null }
              : {
                  start: primaryAdjusted[insertedIndex].start,
                  end: primaryAdjusted[insertedIndex].end,
                  value: 'no data' as any,
                  label: '-',
                  tooltipExtraData: [],
                  name: s[0]?.name || `Series ${si + 1}`,
                  raw: null,
                };
            s.splice(insertedIndex, 0, dummy);
          });
        }
      }
    }

    // Now final aligned series to draw = alignedSeries (all have same length as primaryAdjusted)
    const finalPrimary = alignedSeries[0];

    // compute global numeric domain across all series values
    const allValues = alignedSeries
      .flatMap((s) =>
        s.map((d) => (typeof d.value === 'number' ? d.value : NaN))
      )
      .filter((v) => !isNaN(v));
    const maxValue = allValues.length ? (d3.max(allValues) as number) : 0;
    const yTempScale = d3.scaleLinear().domain([0, maxValue]).nice();
    const yAxisFormatter = d3.format(',');
    const yLabels = yTempScale.ticks().map(yAxisFormatter);
    const widestLabelWidth = this.getWidestLabelWidth(
      yLabels,
      '12px Inter, sans-serif'
    );

    const width = container.clientWidth;
    const height = container.clientHeight;

    if (width === 0 || height === 0) return;

    const marginLeft =
      this.marginProps.left ?? (this.showYAxis ? widestLabelWidth + 12 : 10);
    const marginRight = this.marginProps.right ?? 20;
    const marginTop = this.marginProps.top ?? 20;
    const marginBottom = this.marginProps.bottom ?? (this.showXAxis ? 30 : 10);

    const minDate = d3.min(finalPrimary, (d) => d.start)!;
    const maxDate = d3.max(finalPrimary, (d) => d.start)!;

    const x = d3
      .scaleUtc()
      .domain([minDate, maxDate])
      .range([marginLeft, width - marginRight]);
    const y = d3
      .scaleLinear()
      .domain([0, maxValue])
      .range([height - marginBottom, marginTop])
      .nice();

    const svg = d3
      .select(container)
      .append('svg')
      .attr('width', '100%')
      .attr('height', '100%')
      .attr('viewBox', `0 0 ${width} ${height}`)
      .attr('preserveAspectRatio', 'xMidYMid meet');
    if (this.setBg) svg.style('background', colors.backgroundGradient);

    // Y axis + grid
    svg
      .append('g')
      .attr('transform', `translate(${marginLeft},0)`)
      .call(
        d3
          .axisLeft(y)
          .tickSize(0)
          .tickValues(d3.scaleLinear().domain(y.domain()).ticks(6))
      )
      .call((g) =>
        g
          .selectAll('.tick line')
          .clone()
          .attr('stroke', colors.gridLine)
          .attr('stroke-opacity', 0.4)
          .attr('stroke-dasharray', '4, 4')
          .attr('x2', width - marginLeft - marginRight)
      )
      .call((g) => g.select('.domain').remove())
      .call((g) => g.selectAll('.tick text').remove());

    // Calculate X axis tick values shared by axis and grid
    let xTickValues: any[] = [];
    // When set, tick labels are produced from this function instead of the
    // pre-computed `entry.label`. Used to collapse a long quarterly range
    // into one tick per year so the X-axis does not overflow horizontally.
    let xTickFormatOverride: ((d: Date) => string) | null = null;

    if (this.showAllXLabels) {
      // Special-case: quarterly data that spans more than 3 distinct years
      // produces too many quarter labels (e.g. ~40 ticks for a 10-year
      // "All time" range) and visually breaks the X-axis. In that case we
      // render only one tick per year showing just the year. The full
      // quarter label is still available via the hover tooltip.
      if (this.unit === 'quarter') {
        const distinctYears = new Set(
          finalPrimary.map((d) => d.start.getUTCFullYear())
        );
        if (distinctYears.size > 3) {
          const seenYears = new Set<number>();
          finalPrimary.forEach((d) => {
            const year = d.start.getUTCFullYear();
            if (!seenYears.has(year)) {
              seenYears.add(year);
              xTickValues.push(d.start);
            }
          });
          xTickFormatOverride = (d: Date) => `${d.getUTCFullYear()}`;
        } else {
          xTickValues = finalPrimary.map((d) => d.start);
        }
      } else {
        xTickValues = finalPrimary.map((d) => d.start);
      }
    } else {
      const firstDate = finalPrimary[0]?.start;
      const lastDate = finalPrimary[finalPrimary.length - 1]?.start;
      xTickValues = [firstDate, lastDate].filter(Boolean);
    }

    const useSmartXAxisAnchors =
      this.xAxisSmartLabelAnchors && xTickValues.length > 2;

    // X axis
    if (this.showXAxis) {
      const xAxis = d3
        .axisBottom(x)
        .tickValues(xTickValues)
        .tickFormat((d: any) => {
          if (xTickFormatOverride) return xTickFormatOverride(d);
          const entry = finalPrimary.find(
            (cd) => cd.start.getTime() === d.getTime()
          );
          return entry ? entry.label : '';
        });

      const xAxisGroup = svg
        .append('g')
        .attr('class', 'x-axis')
        .attr('transform', `translate(0,${height - marginBottom + 1})`)
        .call(xAxis);
      xAxisGroup.selectAll('path, line').attr('stroke', colors.axis);
      const useYearOnlyAnchors = !!xTickFormatOverride;
      const useSmartAnchors = useSmartXAxisAnchors || useYearOnlyAnchors;
      xAxisGroup
        .selectAll('text')
        .attr('fill', colors.axisTick)
        .style('font-size', '12px')
        .style('font-family', 'Inter, sans-serif')
        .each(function (d, i, nodes) {
          const lastIdx = (nodes as ArrayLike<unknown>).length - 1;
          if (i === 0) {
            d3.select(this).attr('text-anchor', 'start');
          } else if (useSmartAnchors && i !== lastIdx) {
            d3.select(this).attr('text-anchor', 'middle');
          } else {
            d3.select(this).attr('text-anchor', 'end');
          }
        });

      if (this.setBg) {
        const topH = 30;
        const radius = 10;
        const path = `M0,0 H${width} V${topH - radius} Q${width},${topH} ${
          width - radius
        },${topH} H${radius} Q0,${topH} 0,${topH - radius}Z`;
        xAxisGroup
          .insert('path', ':first-child')
          .attr('d', path)
          .attr('fill', colors.xAxisOverlayBg)
          .attr('stroke', '');
      }
    }

    if (this.showYAxis) {
      let yAxis = d3.axisLeft(y).tickFormat(yAxisFormatter);
      if (this.yAxisShowOnlyFirstLast) {
        const yTicks = y.ticks();
        if (this.showAllYticks) yAxis.tickValues(yTicks);
        else yAxis.tickValues([yTicks[0], yTicks[yTicks.length - 1]]);
      }
      const yAxisGroup = svg
        .append('g')
        .attr('transform', `translate(${marginLeft},0)`)
        .call(yAxis);
      if (this.showYAxisPath)
        yAxisGroup.selectAll('path, line').attr('stroke', colors.axis);
      else yAxisGroup.selectAll('path, line').remove();
      yAxisGroup
        .selectAll('text')
        .attr('fill', colors.axisTick)
        .style('font-size', '12px')
        .style('font-family', 'Inter, sans-serif');
    }

    if (this.showYAxisGrid) {
      svg
        .append('g')
        .attr('class', 'grid')
        .attr('transform', `translate(${marginLeft},0)`)
        .call(
          d3
            .axisLeft(y)
            .tickSize(-(width - marginLeft - marginRight))
            .tickFormat(() => '')
        )
        .selectAll('line')
        .attr('stroke', colors.gridLine)
        .attr('stroke-opacity', 1)
        .attr('stroke-dasharray', '2,2');
      svg.select('.grid .domain').remove();
    }

    if (this.showXAxisGrid) {
      const gridHeight = height - marginTop - marginBottom;
      const xAxisY = y.range()[0];
      const xGrid = d3
        .axisBottom(x)
        .tickValues(xTickValues)
        .tickSize(-gridHeight)
        .tickFormat(() => '');
      const xGridGroup = svg
        .append('g')
        .attr('class', 'x-grid')
        .attr('transform', `translate(0, ${xAxisY})`)
        .call(xGrid);
      xGridGroup
        .selectAll('line')
        .attr('stroke', colors.gridLine)
        .attr('stroke-opacity', 1)
        .attr('stroke-dasharray', '4,4');
      xGridGroup.select('.domain').remove();
    }

    // line generator
    const lineGen = d3
      .line<any>()
      .x((d) => x(d.start))
      .y((d) => y(d.value))
      .defined((d) => typeof d.value === 'number')
      .curve(d3[this.curveType]);

    // palette: base on theme colors + extras
    const palette = [
      colors.primaryLine,
      colors.secondaryLine,
      colors.tertiaryLine,
      colors.quaternaryLine,
      colors.quinaryLine,
      colors.senaryLine,
      colors.septenaryLine,
      colors.octonaryLine,
      colors.nonaryLine,
      colors.denaryLine,
    ];

    // create structured lines array
    const lines = alignedSeries.map((dataset, idx) => ({
      data: dataset,
      color: palette[idx % palette.length],
      name: dataset[0]?.name || `Series ${idx + 1}`,
    }));

    // draw each line
    lines.forEach((lineObj, i) => {
      // shadow optionally for first two series to mimic previous behavior: keep simple - no shadow here
      // draw path
      svg
        .append('path')
        .datum(lineObj.data)
        .attr('fill', 'none')
        .attr('stroke', lineObj.color)
        .attr('stroke-width', 3)
        .attr('d', lineGen as any);
    });

    // draw circles per series (normal and outlined if requested)
    lines.forEach((lineObj, lineIndex) => {
      if (this.showCircleStrokes) {
        svg
          .selectAll(`circle.series-${lineIndex}`)
          .data(lineObj.data.filter((d) => typeof d.value === 'number'))
          .join('circle')
          .attr('class', `series-${lineIndex}`)
          .attr('cx', (d) => x(d.start))
          .attr('cy', (d) => y(d.value))
          .attr('r', 4)
          .attr('fill', lineObj.color)
          .attr('stroke', lineObj.color)
          .attr('stroke-width', 2);
      }
      if (this.showCircleStrokesWithStroke && !this.strokeWithLineColor) {
        svg
          .selectAll(`circle.outlined-${lineIndex}`)
          .data(lineObj.data.filter((d) => typeof d.value === 'number'))
          .join('circle')
          .attr('class', `outlined-${lineIndex}`)
          .attr('cx', (d) => x(d.start))
          .attr('cy', (d) => y(d.value))
          .attr('r', 6)
          .attr('fill', lineObj.color)
          .attr('stroke', '#fff')
          .attr('stroke-width', 2);
      }

      if (this.strokeWithLineColor && !this.showCircleStrokesWithStroke) {
        let strokeFillColor = '';
        if (this.strokeWithLineColor) {
          const isDarkTheme = this.currentTheme === 'dark';
          strokeFillColor = isDarkTheme ? '#151517' : '#fff';
        } else {
          strokeFillColor = '#fff';
        }

        svg
          .selectAll(`circle.outlined-${lineIndex}`)
          .data(lineObj.data.filter((d) => typeof d.value === 'number'))
          .join('circle')
          .attr('class', `outlined-${lineIndex}`)
          .attr('cx', (d) => x(d.start))
          .attr('cy', (d) => y(d.value))
          .attr('r', 6)
          .attr('fill', strokeFillColor)
          .attr('stroke', this.strokeWithLineColor ? lineObj.color : '#fff')
          .attr('stroke-width', 2);
      }
    });

    // tooltip setup
    const t = this.getTooltipStyleByTheme(colors);
    const tooltip = d3
      .select(container)
      .append('div')
      .attr('class', 'tooltip')
      .style('opacity', 0)
      .style('position', 'absolute')
      .style('pointer-events', 'none')
      .style('background', t.background)
      .style('border', t.border)
      .style('border-radius', '10px')
      .style('padding', '12px 16px')
      .style('box-shadow', t.shadow)
      .style('color', t.text)
      .style('font-family', 'Inter, sans-serif')
      .style('white-space', 'nowrap')
      .style('transition', 'opacity 0.15s ease');

    const hoverGroup = svg
      .append('g')
      .attr('class', 'hover-group')
      .style('display', 'none');
    hoverGroup
      .append('line')
      .attr('class', 'hover-line')
      .attr('y1', marginTop)
      .attr('y2', height - marginBottom)
      .attr('stroke', colors.hoverLine)
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '2,2');
    hoverGroup
      .append('text')
      .attr('class', 'hover-label')
      .attr('y', height - marginBottom + 20)
      .attr('text-anchor', 'middle')
      .attr('fill', colors.axisLabel)
      .style('font-size', '12px');

    svg
      .append('rect')
      .attr('class', 'overlay')
      .attr('x', marginLeft)
      .attr('y', marginTop)
      .attr('width', width - marginLeft - marginRight)
      .attr('height', height - marginTop - marginBottom)
      .attr('fill', 'transparent')
      .on('mouseover', () => {
        hoverGroup.style('display', null);
        tooltip.style('opacity', 1);
        svg.selectAll('.x-axis .tick text').style('opacity', 0);
      })
      .on('mouseout', () => {
        hoverGroup.style('display', 'none');
        tooltip.style('opacity', 0);
        svg.selectAll('.x-axis .tick text').style('opacity', 1);
      })
      .on('mousemove', (event: MouseEvent) => {
        const [mx, my] = d3.pointer(event);
        const hoveredDate = x.invert(mx);
        // use bisector on primary series
        const bisector = d3.bisector((d: any) => d.start).left;
        const idx = bisector(finalPrimary, hoveredDate, 1);
        const d0 = finalPrimary[idx - 1];
        const d1 = finalPrimary[idx];
        const d = !d0
          ? d1
          : !d1
          ? d0
          : hoveredDate.getTime() - d0.start.getTime() >
            d1.start.getTime() - hoveredDate.getTime()
          ? d1
          : d0;
        if (!d) return;

        const dataX = x(d.start);
        hoverGroup.select('.hover-line').attr('x1', dataX).attr('x2', dataX);
        const hoverLabel = hoverGroup.select('.hover-label').text(d.label);
        const labelWidth = (hoverLabel.node() as SVGTextElement).getBBox()
          .width;
        let labelX = dataX;
        if (labelX - labelWidth / 2 < marginLeft)
          labelX = marginLeft + labelWidth / 2;
        else if (labelX + labelWidth / 2 > width - marginRight)
          labelX = width - marginRight - labelWidth / 2;
        hoverLabel.attr('x', labelX);

        // Build tooltip rows for every series at this index
        const seriesRows: { name: string; color: string; value: any }[] = [];
        lines.forEach((lineObj, sIndex) => {
          // find corresponding point in this series aligned to primary index
          const point =
            alignedSeries[sIndex][finalPrimary.indexOf(d)] ||
            alignedSeries[sIndex].find(
              (pt) => pt.start.getTime() === d.start.getTime()
            );
          const val = point
            ? point.value
            : lineObj.data[finalPrimary.indexOf(d)]?.value ?? '-';
          seriesRows.push({
            name: lineObj.name || `Series ${sIndex + 1}`,
            color: lineObj.color,
            value:
              typeof val === 'number'
                ? val
                : isNaN(Number(val))
                ? val
                : Number(val),
          });
        });

        // Build tooltip HTML
        let tooltipHTML = `<div style="display:flex;flex-direction:column;gap:8px;">`;
        tooltipHTML += `<div style="font-size:15px;font-weight:600;color:${t.text};margin-bottom:4px;">${d.label}</div>`;
        seriesRows.forEach((row) => {
          tooltipHTML += `
          <div style="display:flex;justify-content:space-between;align-items:center;gap:12px;font-size:13px;">
            <div style="display:flex;align-items:center;gap:8px;">
              <span style="color:${row.color};font-weight:500;">${
            row.name
          }</span>
            </div>
            <div style="font-weight:700;color:${row.color};">${
            row.value ?? '-'
          }</div>
          </div>
        `;
        });
        tooltipHTML += `</div>`;
        tooltip.html(tooltipHTML);

        // position tooltip near pointer
        const tooltipNode = tooltip.node() as HTMLElement;
        const tooltipRect = tooltipNode.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        // pointer coords relative to container
        const ptrX = mx + containerRect.left;
        const ptrY = my + containerRect.top;

        let left = ptrX + 15;
        if (left + tooltipRect.width > containerRect.left + width)
          left = ptrX - 15 - tooltipRect.width;
        let top = ptrY - 10 - tooltipRect.height;
        if (top < containerRect.top) top = containerRect.top;

        tooltip
          .style('left', `${left - containerRect.left}px`)
          .style('top', `${top - containerRect.top}px`);
      });
  }

  getTooltipStyleByTheme(colors: any) {
    const isDark = this.currentTheme === 'dark';

    return {
      background: isDark ? '#232327' : '#F5F5F5',
      text: isDark ? '#ffffff' : '#141437',
      shadow: isDark
        ? '0px 4px 20px rgba(0,0,0,0.35)'
        : '0px 4px 20px rgba(0,0,0,0.15)',
      border: isDark ? '1px solid #2A2A2E' : '1px solid #EAEAEA',
      subtitleColor: '#AD46FF',
    };
  }
}
